const REDACTED_KEYS = [
  'email',
  'phone',
  'address',
  'token',
  'secret',
  'password',
  'fullName',
  'full_name',
  'story',
  'coping',
  'wishKnown',
  'wish_known',
  'location',
];

let monitoringInitialized = false;
let sentryModule = null;
let sentryModulePromise = null;
let monitoringInitPromise = null;

async function getSentry() {
  if (sentryModule) {
    return sentryModule;
  }

  if (!sentryModulePromise) {
    sentryModulePromise = import('@sentry/react').then((module) => {
      sentryModule = module;
      return module;
    });
  }

  return sentryModulePromise;
}

function getSampleRate(value, fallback) {
  const parsed = Number.parseFloat(String(value || ''));
  if (!Number.isFinite(parsed) || parsed < 0 || parsed > 1) {
    return fallback;
  }

  return parsed;
}

function shouldRedactKey(key) {
  return REDACTED_KEYS.includes(String(key || ''));
}

function sanitizeValue(value, depth = 0) {
  if (value == null) return value;
  if (depth > 4) return '[truncated]';

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
    };
  }

  if (typeof value === 'string') {
    return value.length > 500 ? `${value.slice(0, 497)}...` : value;
  }

  if (Array.isArray(value)) {
    return value.slice(0, 20).map((item) => sanitizeValue(item, depth + 1));
  }

  if (typeof value === 'object') {
    return Object.entries(value).reduce((acc, [key, entryValue]) => {
      acc[key] = shouldRedactKey(key) ? '[redacted]' : sanitizeValue(entryValue, depth + 1);
      return acc;
    }, {});
  }

  return value;
}

function shouldCaptureToMonitoring() {
  return process.env.NODE_ENV !== 'development' || process.env.GATSBY_SENTRY_CAPTURE_DEV === 'true';
}

export function initializeClientMonitoring() {
  if (monitoringInitialized || typeof window === 'undefined') {
    return Promise.resolve(monitoringInitialized);
  }

  const dsn = process.env.GATSBY_SENTRY_DSN;
  if (!dsn) {
    return Promise.resolve(false);
  }

  if (!monitoringInitPromise) {
    monitoringInitPromise = getSentry()
      .then((Sentry) => {
        Sentry.init({
          dsn,
          environment:
            process.env.GATSBY_SENTRY_ENVIRONMENT || process.env.NODE_ENV || 'production',
          release: process.env.GATSBY_SENTRY_RELEASE || undefined,
          sendDefaultPii: false,
          sampleRate: getSampleRate(process.env.GATSBY_SENTRY_SAMPLE_RATE, 1),
          tracesSampleRate: getSampleRate(process.env.GATSBY_SENTRY_TRACES_SAMPLE_RATE, 0),
          beforeSend(event) {
            if (event.user) {
              delete event.user;
            }

            if (event.request?.headers?.cookie) {
              delete event.request.headers.cookie;
            }

            return event;
          },
        });

        monitoringInitialized = true;
        return true;
      })
      .catch(() => false);
  }

  return monitoringInitPromise;
}

export function captureClientError(error, context = {}) {
  if (!shouldCaptureToMonitoring() || !monitoringInitialized || !sentryModule) {
    return;
  }

  const {
    message,
    tags = {},
    extra,
    level = 'error',
    fingerprint,
    context: namedContext,
  } = context;

  sentryModule.withScope((scope) => {
    scope.setLevel(level);

    Object.entries(tags).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        scope.setTag(key, String(value));
      }
    });

    if (message) {
      scope.setTag('reported_message', String(message));
    }

    if (extra) {
      scope.setExtras(sanitizeValue(extra));
    }

    if (namedContext) {
      scope.setContext('app_context', sanitizeValue(namedContext));
    }

    if (Array.isArray(fingerprint) && fingerprint.length > 0) {
      scope.setFingerprint(fingerprint.map(String));
    }

    if (error instanceof Error) {
      sentryModule.captureException(error);
    } else {
      sentryModule.captureMessage(String(message || error || 'Client error reported'), level);
    }
  });
}
